/**
 * api_v2/subscription.ts
 * Подписка и платежи для API v2.
 *
 * КЛЮЧЕВЫЕ ОТЛИЧИЯ от старого useSubscriptionLogic.js:
 * 1. user_id НЕ передаётся — сервер берёт его из токена
 * 2. amount НЕ передаётся — сервер определяет цену по plan_id (нельзя подменить)
 * 3. payment_url валидируется перед редиректом (V20)
 */

import client from "./client";

export interface SubscriptionStatus {
  type?: string | null;
  start?: string | null;
  end?: string | null;
  duration?: number;
  is_active: boolean;
}

export interface SubscriptionUser {
  id: number;
  first_name?: string;
  last_name?: string;
  phone?: string;
}

export interface PaymentResult {
  success: boolean;
  payment_url?: string;
  payment_id?: string;
  charged?: boolean;
  fallback?: boolean;
  status?: string;
  message?: string;
}

/** Разрешённые домены для редиректа оплаты */
const ALLOWED_PAYMENT_DOMAINS = [
  "yookassa.ru",
  "pay.yookassa.ru",
  "yoomoney.ru",
  "securepay.tinkoff.ru",
  "tinkoff.ru",
];

/**
 * Проверяет что payment_url безопасен для редиректа.
 * Только HTTPS, только разрешённые домены платёжных систем.
 */
export function isValidPaymentUrl(url: string): boolean {
  try {
    const u = new URL(url);
    if (u.protocol !== "https:") return false;
    return ALLOWED_PAYMENT_DOMAINS.some(
      (domain) => u.hostname === domain || u.hostname.endsWith("." + domain)
    );
  } catch {
    return false;
  }
}

/** Получить статус подписки текущего пользователя (user_id из токена) */
export async function getSubscriptionStatus(): Promise<{
  success: boolean;
  subscription_status: "active" | "expired" | "none";
  subscription?: SubscriptionStatus;
  user?: SubscriptionUser;
  auto_payment?: Record<string, unknown> | null;
  billing_violation?: Record<string, unknown> | null;
}> {
  const { data } = await client.post("/subscription/get_status.php", {});
  return data;
}

/** Публичные тарифы. Сумма и срок используются только для отображения. */
export async function getPublicPlans(): Promise<PlansResponse> {
  const { data } = await client.get<PlansResponse>("/subscription/public_plans.php");
  return data;
}

/**
 * Генерирует непредсказуемый ключ одной попытки оплаты.
 * Если Web Crypto недоступен, заголовок не отправляется и native backend
 * создаёт безопасный server-side idempotency key.
 */
export function createPaymentIdempotencyKey(): string | null {
  const webCrypto = globalThis.crypto;
  if (!webCrypto) return null;
  if (typeof webCrypto.randomUUID === "function") {
    return `web:${webCrypto.randomUUID()}`;
  }
  if (typeof webCrypto.getRandomValues !== "function") return null;

  const bytes = webCrypto.getRandomValues(new Uint8Array(16));
  const random = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
  return `web:${random}`;
}

/**
 * Создать платёж.
 * @param planId — ID тарифного плана (сервер сам знает его цену)
 * Фронт НЕ передаёт amount — исключает подмену суммы (V05, IDOR)
 */
export async function createPayment(
  planId: string
): Promise<PaymentResult> {
  const idempotencyKey = createPaymentIdempotencyKey();
  const { data } = await client.post<PaymentResult>(
    "/subscription/create_payment.php",
    { plan_id: planId },
    idempotencyKey ? { headers: { "Idempotency-Key": idempotencyKey } } : undefined
  );
  return data;
}

/**
 * Продлить/оплатить тариф:
 * сервер сначала пробует списать сохранённой картой, а если не получается —
 * возвращает payment_url для обычной оплаты.
 */
export async function payOrCreatePayment(
  planId: string
): Promise<PaymentResult> {
  const { data } = await client.post<PaymentResult>(
    "/subscription/pay_or_create_payment.php",
    { plan_id: planId }
  );
  return data;
}

/**
 * Создать платёж и сделать редирект на страницу оплаты.
 * Валидирует URL перед редиректом (V20).
 */
export async function createPaymentAndRedirect(
  planId: string
): Promise<{ success: false; message: string } | { success: true }> {
  const result = await createPayment(planId);

  if (!result.success) {
    return { success: false, message: result.message ?? "Ошибка создания платежа" };
  }

  if (!result.payment_url) {
    return { success: false, message: "Сервер не вернул ссылку на оплату" };
  }

  if (!isValidPaymentUrl(result.payment_url)) {
    console.error("Подозрительный payment_url отклонён:", result.payment_url);
    return { success: false, message: "Ошибка: некорректный адрес платёжной страницы" };
  }

  window.location.href = result.payment_url;
  return { success: true };
}

export async function payOrRedirect(
  planId: string
): Promise<{ success: false; message: string } | { success: true; charged?: boolean; fallback?: boolean }> {
  const result = await payOrCreatePayment(planId);

  if (!result.success) {
    return { success: false, message: result.message ?? "Ошибка оплаты" };
  }

  if (result.charged && !result.payment_url) {
    return { success: true, charged: true, fallback: false };
  }

  if (!result.payment_url) {
    return { success: false, message: "Сервер не вернул ссылку на оплату" };
  }

  if (!isValidPaymentUrl(result.payment_url)) {
    console.error("Подозрительный payment_url отклонён:", result.payment_url);
    return { success: false, message: "Ошибка: некорректный адрес платёжной страницы" };
  }

  window.location.href = result.payment_url;
  return { success: true, charged: false, fallback: true };
}

/** Получить реферальный код текущего пользователя (user_id из токена, не из URL) */
export async function getReferralCode(): Promise<{
  success: boolean;
  referral_code?: string;
}> {
  // POST вместо GET — user_id не торчит в URL (V19)
  const { data } = await client.post("/user/get_referral.php", {});
  return data;
}

// ── Тарифы и смена плана ─────────────────────────────────────────────────────

export interface SubscriptionPlan {
  id: string;
  name: string;
  months: number;
  amount: number;
}

export interface CurrentAutoPay {
  plan_id: string | null;
  amount: number;
  months: number;
  next_payment_date: string;
  status: string;
}

export interface PlansResponse {
  success: boolean;
  plans: SubscriptionPlan[];
  current_auto_payment: CurrentAutoPay | null;
}

export interface ChangeAutoPaymentResponse {
  success: boolean;
  message?: string;
  new_plan?: {
    plan_id: string;
    name: string;
    amount: number;
    months: number;
    next_payment_date: string;
  };
}

export interface SavedPaymentCard {
  masked_pan: string;
  card_type?: string | null;
  expiry_date?: string | null;
}

export interface CardInfoResponse {
  success: boolean;
  cards: SavedPaymentCard[];
  message?: string;
}

/** Получить список тарифов из БД + текущий автоплатёж пользователя */
export async function getPlans(): Promise<PlansResponse> {
  const { data } = await client.post<PlansResponse>("/subscription/get_plans.php", {});
  return data;
}

/** Сменить тариф автоплатежа (без немедленной оплаты) */
export async function changeAutoPayment(planId: string): Promise<ChangeAutoPaymentResponse> {
  const { data } = await client.post<ChangeAutoPaymentResponse>(
    "/subscription/change_auto_payment.php",
    { plan_id: planId }
  );
  return data;
}

/** Получить сохранённые карты текущего пользователя */
export async function getCardInfo(): Promise<CardInfoResponse> {
  const { data } = await client.post<CardInfoResponse>("/user/get_card_info.php", {});
  return data;
}

/** Отвязать карту */
export async function unlinkCard(): Promise<{ success: boolean; message?: string }> {
  const { data } = await client.post("/user/unlink_card.php", {});
  return data;
}
